import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, unref as _unref } from "vue";
const _hoisted_1 = {
  class: "d-flex align-center justify-space-between flex-wrap gap-2 px-1 pb-2"
};
const _hoisted_2 = {
  class: "text-h6 font-weight-bold"
};
const _hoisted_3 = {
  class: "text-body-2 text-grey-darken-1 mb-4 px-1"
};
const _hoisted_4 = {
  key: 1,
  class: "mb-3 rounded border border-orange-300 bg-orange-50 px-3 py-2 text-sm text-orange-700",
  "data-cy": "narrative-unsupported-keywords"
};
const _hoisted_5 = {
  class: "mb-4"
};
const _hoisted_6 = {
  class: "mb-4"
};
const _hoisted_7 = {
  class: "d-flex align-center flex-wrap gap-2 pt-4 border-t border-grey-lighten-3"
};
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { EditorContent, useEditor } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import { useInvoiceTemplateStore } from '@/services/useInvoiceTemplateStore.js';
import { useSettlementStore } from '@/modules/matters/settlement/stores/useSettlementStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import useClientMatterStore from '@/services/useClientMatterStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import AlertBar from '@/components/AlertBar.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import SettlementEditorSection from '@/modules/settings/company/InvoiceEmail/InvoicePdf/OtherPages/SettlementEditorSection.vue';
import { SETTLEMENT_V1_KEYWORDS } from '@/constants/settlementKeywordConstants';
import { enrichBackendKeywordsWithMetadata, findUnsupportedSettlementKeywords, settlementToken } from '@/utils/keywords';
import { sanitizeEditorHtml, normalizeEditorValue, isEmptyHtml } from '@/utils/editorHtml';
import SETTLEMENT_EDITOR_CLASS from '@/utils/editorClass';
const FIRM_TEMPLATE_DEFAULT_ID = 'settlement-statement-template';
const PREVIEW_DEBOUNCE_MS = 3000;
export default {
  __name: 'SettlementNarrativeSection',
  props: {
    /** Number shown in the title — the parent renumbers when Map Payments is skipped. */
    stepNumber: {
      type: Number,
      default: 6
    },
    /** Settlement ID — used to fetch/save the per-matter narrative and manage the editor lock. */
    settlementId: {
      type: String,
      required: true
    },
    /** Disables editing & actions when the settlement is locked. */
    isLocked: {
      type: Boolean,
      default: false
    },
    /** Whether the Generate action is allowed (all prior steps complete). */
    canGenerate: {
      type: Boolean,
      default: true
    },
    /** Loading state for the Generate button (driven by the parent's generate flow). */
    saving: {
      type: Boolean,
      default: false
    }
  },
  emits: ['generate', 'go-back'],
  setup(__props) {
    const props = __props;
    const settlementStore = useSettlementStore();
    const invoiceTemplateStore = useInvoiceTemplateStore();
    const meStore = useMeStore();
    const clientMatterStore = useClientMatterStore();
    const {
      handleSuccess,
      handleError
    } = useSnackbarHandler();

    // Get configurable terms (Matter, Case, Deal, etc.) from firm settings
    const {
      terms
    } = storeToRefs(meStore);
    const matterTerm = computed(() => terms.value?.matter || 'matter');

    // Name of the user holding the editor lock — but only when it's *someone else*.
    // A lock owned by the current account (e.g. our own lock returned by
    // fetchNarrative, or renewed on re-acquire) must not lock us out of editing.
    const lockedByName = computed(() => {
      const lock = settlementStore.narrative.editorLock;
      if (!lock) return null;
      if (lock.lockedByAccountId && lock.lockedByAccountId === meStore.me?.id) {
        return null;
      }
      return lock.lockedByName ?? null;
    });

    // Default narrative used when neither the matter nor the firm has saved content.
    const DEFAULT_OPENING_PARAGRAPH = `Dear ${settlementToken('client_name')},\n\nThis statement summarizes the disbursement of funds from the settlement of ${settlementToken('matter_name')}. The total gross recovery is ${settlementToken('gross_recovery')}. Of this amount, our firm's contingent attorney fee is ${settlementToken('attorney_fee')}, and total expenses advanced on your behalf are ${settlementToken('total_expenses')}.`;
    const DEFAULT_CLOSING_PARAGRAPH = `After payment of fees, expenses, and the liens listed above (${settlementToken('total_liens')} in total), your net recovery is ${settlementToken('net_to_client')}.\n\nBy signing below, you acknowledge that the fee and expenses shown are consistent with the contingent fee agreement you signed with our firm.`;
    const restoreDialog = ref(null);
    const savingDraft = ref(false);
    const hasFirmTemplate = ref(false);
    // Set during programmatic content sync so editor onUpdate doesn't flag customization.
    let isHydrating = false;
    const narrative = reactive({
      enableOpeningParagraph: true,
      openingParagraph: '<p></p>',
      enableClosingParagraph: true,
      closingParagraph: '<p></p>',
      isCustomized: false,
      keywords: []
    });
    const readOnly = computed(() => props.isLocked || !!lockedByName.value);
    const isCustomized = computed(() => narrative.isCustomized);

    // ── Editors ──
    const onEditorEdit = () => {
      if (isHydrating) return;
      narrative.isCustomized = true;
      // Dim the live preview immediately, then auto-save + regenerate the PDF after
      // the user stops typing (debounced).
      settlementStore.markNarrativeStale();
      schedulePreview();
    };

    // Toggling a section on/off is an edit: flag it customized, persist it, and
    // regenerate the preview so the change is reflected and survives a reload.
    // Without this the switch only mutated local state — the preview never updated
    // and the disabled flag was lost on the next fetchNarrative.
    const onToggleEnabled = (key, value) => {
      if (readOnly.value) return;
      narrative[key] = value;
      onEditorEdit();
    };
    const buildEditor = onContent => useEditor({
      extensions: [StarterKit.configure({
        blockquote: false,
        code: false,
        codeBlock: false,
        hardBreak: false,
        heading: false,
        horizontalRule: false,
        orderedList: false,
        strike: false,
        history: true
      })],
      content: '<p></p>',
      editable: !readOnly.value,
      editorProps: {
        attributes: {
          class: SETTLEMENT_EDITOR_CLASS
        },
        transformPastedHTML: html => sanitizeEditorHtml(html)
      },
      onUpdate: ({
        editor
      }) => {
        onContent(editor.getHTML());
        onEditorEdit();
      }
    });
    const openingEditor = buildEditor(html => {
      narrative.openingParagraph = html;
    });
    const closingEditor = buildEditor(html => {
      narrative.closingParagraph = html;
    });
    const syncEditorsFromState = () => {
      isHydrating = true;
      openingEditor.value?.commands.setContent(narrative.openingParagraph, false);
      closingEditor.value?.commands.setContent(narrative.closingParagraph, false);
      isHydrating = false;
    };
    const applyEditable = () => {
      openingEditor.value?.setEditable(!readOnly.value);
      closingEditor.value?.setEditable(!readOnly.value);
    };
    watch(readOnly, applyEditable);

    // ── Keywords ──
    const keywordGroups = computed(() => {
      const groups = new Map();
      const keywords = narrative.keywords.length > 0 ? narrative.keywords : SETTLEMENT_V1_KEYWORDS;
      keywords.forEach(keyword => {
        const groupName = keyword.group || 'General';
        if (!groups.has(groupName)) groups.set(groupName, []);
        groups.get(groupName).push(keyword);
      });
      return Array.from(groups.entries()).map(([group, list]) => ({
        group,
        keywords: list
      }));
    });
    const unsupportedKeywords = computed(() => {
      const tokens = [...findUnsupportedSettlementKeywords(narrative.openingParagraph), ...findUnsupportedSettlementKeywords(narrative.closingParagraph)];
      return tokens.filter((token, index) => tokens.indexOf(token) === index);
    });
    const resolveEditor = ref_ => ref_ ? ref_.value || ref_ : null;
    const insertKeyword = (editorRef, token) => {
      const editor = resolveEditor(editorRef);
      if (!editor) return;
      editor.chain().focus().insertContent(` ${token} `).run();
    };
    const isMarkActive = (editorRef, mark) => resolveEditor(editorRef)?.isActive(mark) ?? false;
    const isNodeActive = (editorRef, node) => resolveEditor(editorRef)?.isActive(node) ?? false;
    const toggleMark = (editorRef, markType) => {
      const editor = resolveEditor(editorRef);
      if (!editor) return;
      const chain = editor.chain().focus();
      if (markType === 'bold') chain.toggleBold().run();else if (markType === 'italic') chain.toggleItalic().run();
    };
    const toggleBulletList = editorRef => resolveEditor(editorRef)?.chain().focus().toggleBulletList().run();
    const undo = editorRef => resolveEditor(editorRef)?.chain().focus().undo().run();
    const redo = editorRef => resolveEditor(editorRef)?.chain().focus().redo().run();

    // ── Narrative hydration from store ──
    const hydrateFromNarrative = () => {
      const n = settlementStore.narrative;
      narrative.enableOpeningParagraph = n.enableOpeningParagraph;
      narrative.enableClosingParagraph = n.enableClosingParagraph;
      narrative.openingParagraph = isEmptyHtml(n.openingParagraphHtml) ? normalizeEditorValue(DEFAULT_OPENING_PARAGRAPH) : normalizeEditorValue(n.openingParagraphHtml ?? '');
      narrative.closingParagraph = isEmptyHtml(n.closingParagraphHtml) ? normalizeEditorValue(DEFAULT_CLOSING_PARAGRAPH) : normalizeEditorValue(n.closingParagraphHtml ?? '');
      narrative.isCustomized = n.isCustomized;
      syncEditorsFromState();
    };
    const loadKeywords = async () => {
      try {
        // Scope to this settlement so the backend returns real per-matter values.
        const backendKeywords = await invoiceTemplateStore.getSettlementStatementKeywords(props.settlementId);
        narrative.keywords = enrichBackendKeywordsWithMetadata(backendKeywords);
      } catch {
        narrative.keywords = [];
      }
    };

    // The firm template store masks a 404 with a default template whose id is the
    // literal 'settlement-statement-template' sentinel; a real saved template comes
    // back with a backend GUID. Treat only the latter as "the firm has a template".
    // NOTE: heuristic — replace with a dedicated API flag if one becomes available.
    const checkHasFirmTemplate = async () => {
      try {
        const template = await invoiceTemplateStore.getSettlementStatementTemplate();
        hasFirmTemplate.value = !!template && template.id !== FIRM_TEMPLATE_DEFAULT_ID;
      } catch {
        hasFirmTemplate.value = false;
      }
    };

    // ── Actions ──
    const buildNarrativePayload = () => ({
      enableOpeningParagraph: narrative.enableOpeningParagraph,
      openingParagraphHtml: sanitizeEditorHtml(narrative.openingParagraph),
      enableClosingParagraph: narrative.enableClosingParagraph,
      closingParagraphHtml: sanitizeEditorHtml(narrative.closingParagraph),
      // The matter editor doesn't expose the jurisdiction note — pass through the
      // value loaded from the narrative so saving never wipes it.
      jurisdictionNote: settlementStore.narrative.jurisdictionNote ?? null
    });
    const saveDraft = async () => {
      // Also bail while the debounced autosave (saveAndPreview) is mid-flight — both
      // call settlementStore.saveNarrative() independently with no request-dedup
      // between them, so letting them race can land the manual save's response after
      // the autosave's (or vice versa) and surface a spurious "Unable to save draft"
      // error even though the content was saved successfully. Pre-existing race, not
      // introduced by the Colada migration — neither call site guarded against the
      // other before this fix either.
      if (readOnly.value || previewing.value) return;
      if (unsupportedKeywords.value.length) {
        handleError(`Remove unsupported keywords: ${unsupportedKeywords.value.join(', ')}`);
        return;
      }
      // Cancel any pending (not-yet-fired) debounced autosave — this manual save
      // supersedes it; the scheduled saveAndPreview() would otherwise still fire
      // and race this call once its 3s timer elapses.
      if (previewTimer) {
        clearTimeout(previewTimer);
        previewTimer = null;
      }
      savingDraft.value = true;
      try {
        await settlementStore.saveNarrative(props.settlementId, buildNarrativePayload());
        narrative.isCustomized = settlementStore.narrative.isCustomized;
        handleSuccess('Draft saved.');
      } catch {
        handleError('Unable to save draft. Please try again.');
      } finally {
        savingDraft.value = false;
      }
    };

    // ── Live preview (silent, auto-refreshing) ──
    const previewing = ref(false);
    let previewTimer = null;

    // Generate the preview PDF from the currently-persisted narrative (no save).
    // Used on mount and after restore, where the backend already holds the content.
    const generatePreviewOnly = async () => {
      if (readOnly.value || previewing.value) return;
      previewing.value = true;
      try {
        // generatePreview() short-circuits when a blob already exists, so drop the
        // previous preview to force a fresh render of the latest content.
        settlementStore.revokePreviewBlobUrl();
        await settlementStore.generatePreview();
        settlementStore.markNarrativeFresh();
      } catch {
        // Silent — the preview card surfaces the generating/timed-out state.
      } finally {
        previewing.value = false;
      }
    };

    // Persist the narrative then regenerate the preview. Used by the debounced
    // auto-refresh after the user edits. Skips silently on lock / invalid keywords,
    // and on a manual save already in flight (see saveDraft) to avoid racing it.
    const saveAndPreview = async () => {
      if (readOnly.value || previewing.value || savingDraft.value) return;
      if (unsupportedKeywords.value.length) return;
      previewing.value = true;
      try {
        await settlementStore.saveNarrative(props.settlementId, buildNarrativePayload());
        narrative.isCustomized = settlementStore.narrative.isCustomized;
        settlementStore.revokePreviewBlobUrl();
        await settlementStore.generatePreview();
        settlementStore.markNarrativeFresh();
      } catch {
        // Silent — auto-save/preview failures shouldn't interrupt typing.
      } finally {
        previewing.value = false;
      }
    };

    // Debounce auto-refresh so we only save + regenerate once the user pauses.
    const schedulePreview = () => {
      if (previewTimer) clearTimeout(previewTimer);
      previewTimer = setTimeout(() => {
        previewTimer = null;
        saveAndPreview();
      }, PREVIEW_DEBOUNCE_MS);
    };
    const restoreFromTemplate = async () => {
      try {
        await settlementStore.restoreNarrativeFromTemplate(props.settlementId);
        hydrateFromNarrative();
        handleSuccess('Restored from firm template.');
        // Restore already persisted the firm template — just regenerate the preview.
        generatePreviewOnly();
      } catch {
        // Error handled by store
      }
    };
    const confirmRestore = async () => {
      if (readOnly.value) return;
      const confirmed = await restoreDialog.value?.open();
      if (confirmed) await restoreFromTemplate();
    };
    onMounted(async () => {
      try {
        await Promise.all([settlementStore.fetchNarrative(props.settlementId), loadKeywords(), checkHasFirmTemplate()]);
        hydrateFromNarrative();
        applyEditable();
        // Acquire lock after narrative is loaded — 409 is silently swallowed in the
        // store (editorLock already populated by fetchNarrative), which triggers the
        // read-only banner via the lockedByName computed above.
        await settlementStore.acquireEditorLock(props.settlementId);
        // Kick off the initial preview so the card is "always ready to review".
        generatePreviewOnly();
      } catch {
        handleError('Unable to load the settlement statement. Please reload the page.');
      }
    });
    onBeforeUnmount(async () => {
      if (previewTimer) clearTimeout(previewTimer);
      openingEditor.value?.destroy();
      closingEditor.value?.destroy();
      // Fire-and-forget — Vue doesn't await unmount hooks. Swallow errors so a
      // failed DELETE during navigation doesn't surface as an unhandled rejection.
      try {
        await settlementStore.releaseEditorLock(props.settlementId);
      } catch {
        // lock will expire server-side regardless
      }
    });
    return (_ctx, _cache) => {
      const _component_v_icon = _resolveComponent("v-icon");
      const _component_v_spacer = _resolveComponent("v-spacer");
      const _component_v_card_text = _resolveComponent("v-card-text");
      const _component_v_card = _resolveComponent("v-card");
      return _openBlock(), _createBlock(_component_v_card, {
        flat: "",
        class: "border-0 !bg-[rgb(var(--v-theme-surface))] overflow-hidden p-2"
      }, {
        default: _withCtx(() => [_createVNode(_component_v_card_text, {
          class: "pa-0"
        }, {
          default: _withCtx(() => [_createElementVNode("div", _hoisted_1, [_createElementVNode("div", _hoisted_2, " Step " + _toDisplayString(__props.stepNumber) + ": Review & Customize Statement ", 1), _createElementVNode("span", {
            class: _normalizeClass(["d-inline-flex align-center gap-1 px-3 py-1 rounded-pill text-body-2 font-weight-medium", isCustomized.value ? 'bg-blue-lighten-5 text-blue-darken-2' : 'bg-grey-lighten-3 text-grey-darken-2']),
            "data-cy": "narrative-customized-badge"
          }, [_createVNode(_component_v_icon, {
            size: "14"
          }, {
            default: _withCtx(() => [_createTextVNode(_toDisplayString(isCustomized.value ? 'mdi-pencil' : 'mdi-check'), 1)]),
            _: 1
          }), _createTextVNode(" " + _toDisplayString(isCustomized.value ? `Customized for this ${matterTerm.value}` : 'Matches firm template'), 1)], 2)]), lockedByName.value ? (_openBlock(), _createBlock(AlertBar, {
            key: 0,
            type: "warning",
            class: "mb-4",
            "data-cy": "narrative-lock-banner"
          }, {
            default: _withCtx(() => [...(_cache[16] || (_cache[16] = [_createTextVNode(" Another user is currently editing this statement. The narrative is read-only until they are done. ", -1)]))]),
            _: 1
          })) : _createCommentVNode("", true), _createElementVNode("p", _hoisted_3, " Tailor the narrative for this " + _toDisplayString(matterTerm.value) + ". Numbers always come from the calculator and refresh automatically. Click Generate Settlement Statement when you're ready to lock the statement. ", 1), unsupportedKeywords.value.length ? (_openBlock(), _createElementBlock("div", _hoisted_4, " Unsupported keywords: " + _toDisplayString(unsupportedKeywords.value.join(', ')), 1)) : _createCommentVNode("", true), _createElementVNode("div", _hoisted_5, [_createVNode(SettlementEditorSection, {
            title: "Opening paragraph",
            enabled: !readOnly.value && narrative.enableOpeningParagraph,
            "keyword-groups": keywordGroups.value,
            "bold-active": isMarkActive(_unref(openingEditor), 'bold'),
            "italic-active": isMarkActive(_unref(openingEditor), 'italic'),
            "bullet-list-active": isNodeActive(_unref(openingEditor), 'bulletList'),
            "onUpdate:enabled": _cache[0] || (_cache[0] = $event => onToggleEnabled('enableOpeningParagraph', $event)),
            onToggleBold: _cache[1] || (_cache[1] = $event => toggleMark(_unref(openingEditor), 'bold')),
            onToggleItalic: _cache[2] || (_cache[2] = $event => toggleMark(_unref(openingEditor), 'italic')),
            onToggleBulletList: _cache[3] || (_cache[3] = $event => toggleBulletList(_unref(openingEditor))),
            onUndo: _cache[4] || (_cache[4] = $event => undo(_unref(openingEditor))),
            onRedo: _cache[5] || (_cache[5] = $event => redo(_unref(openingEditor))),
            onInsertKeyword: _cache[6] || (_cache[6] = $event => insertKeyword(_unref(openingEditor), $event))
          }, {
            default: _withCtx(() => [_unref(openingEditor) ? (_openBlock(), _createBlock(_unref(EditorContent), {
              key: 0,
              editor: _unref(openingEditor)
            }, null, 8, ["editor"])) : _createCommentVNode("", true)]),
            _: 1
          }, 8, ["enabled", "keyword-groups", "bold-active", "italic-active", "bullet-list-active"])]), _createElementVNode("div", _hoisted_6, [_createVNode(SettlementEditorSection, {
            title: "Disclosures & closing",
            enabled: !readOnly.value && narrative.enableClosingParagraph,
            "keyword-groups": keywordGroups.value,
            "bold-active": isMarkActive(_unref(closingEditor), 'bold'),
            "italic-active": isMarkActive(_unref(closingEditor), 'italic'),
            "bullet-list-active": isNodeActive(_unref(closingEditor), 'bulletList'),
            "onUpdate:enabled": _cache[7] || (_cache[7] = $event => onToggleEnabled('enableClosingParagraph', $event)),
            onToggleBold: _cache[8] || (_cache[8] = $event => toggleMark(_unref(closingEditor), 'bold')),
            onToggleItalic: _cache[9] || (_cache[9] = $event => toggleMark(_unref(closingEditor), 'italic')),
            onToggleBulletList: _cache[10] || (_cache[10] = $event => toggleBulletList(_unref(closingEditor))),
            onUndo: _cache[11] || (_cache[11] = $event => undo(_unref(closingEditor))),
            onRedo: _cache[12] || (_cache[12] = $event => redo(_unref(closingEditor))),
            onInsertKeyword: _cache[13] || (_cache[13] = $event => insertKeyword(_unref(closingEditor), $event))
          }, {
            default: _withCtx(() => [_unref(closingEditor) ? (_openBlock(), _createBlock(_unref(EditorContent), {
              key: 0,
              editor: _unref(closingEditor)
            }, null, 8, ["editor"])) : _createCommentVNode("", true)]),
            _: 1
          }, 8, ["enabled", "keyword-groups", "bold-active", "italic-active", "bullet-list-active"])]), _createElementVNode("div", _hoisted_7, [_createVNode(LlSecondaryButton, {
            "data-cy": "narrative-back-button",
            onClick: _cache[14] || (_cache[14] = $event => _ctx.$emit('go-back'))
          }, {
            default: _withCtx(() => [...(_cache[17] || (_cache[17] = [_createTextVNode(" ← Back ", -1)]))]),
            _: 1
          }), _createVNode(_component_v_spacer), hasFirmTemplate.value ? (_openBlock(), _createBlock(LlSecondaryButton, {
            key: 0,
            "prepend-icon": "mdi-restore",
            variant: "outlined",
            disabled: readOnly.value,
            "data-cy": "narrative-restore-button",
            onClick: confirmRestore
          }, {
            default: _withCtx(() => [...(_cache[18] || (_cache[18] = [_createTextVNode(" Restore from firm template ", -1)]))]),
            _: 1
          }, 8, ["disabled"])) : _createCommentVNode("", true), _createVNode(LlSecondaryButton, {
            variant: "outlined",
            loading: savingDraft.value,
            disabled: readOnly.value,
            "data-cy": "narrative-save-draft-button",
            onClick: saveDraft
          }, {
            default: _withCtx(() => [...(_cache[19] || (_cache[19] = [_createTextVNode(" Save draft ", -1)]))]),
            _: 1
          }, 8, ["loading", "disabled"]), _createVNode(LlSecondaryButton, {
            color: "primary",
            variant: "flat",
            "prepend-icon": "mdi-file-document-outline",
            loading: __props.saving,
            disabled: readOnly.value || !__props.canGenerate,
            "data-cy": "narrative-generate-button",
            onClick: _cache[15] || (_cache[15] = $event => _ctx.$emit('generate'))
          }, {
            default: _withCtx(() => [...(_cache[20] || (_cache[20] = [_createTextVNode(" Generate Settlement Statement ", -1)]))]),
            _: 1
          }, 8, ["loading", "disabled"])])]),
          _: 1
        }), _createVNode(LlConfirmationDialog, {
          ref_key: "restoreDialog",
          ref: restoreDialog,
          title: "Restore from firm template?",
          message: "This will discard the matter-specific narrative and replace it with your firm's default settlement statement template. This cannot be undone.",
          "confirm-text": "Restore",
          "show-warning-icon": "",
          "data-cy": "narrative-restore-dialog"
        }, null, 512)]),
        _: 1
      });
    };
  }
};